export interface PartialField {
  value: string;
  /** False while the string is still streaming (no closing quote yet). */
  complete: boolean;
}

const SIMPLE_ESCAPES: Record<string, string> = { '"': '"', '\\': '\\', '/': '/', b: '\b', f: '\f', n: '\n', r: '\r', t: '\t' };

/**
 * Read the string fields of a JSON object that may be cut off anywhere, such as tool-call arguments
 * while they stream. Only top-level string values are returned; anything else is skipped.
 * Never throws: whatever can be decoded so far is returned.
 */
export function readPartialStringFields(json: string): Record<string, PartialField> {
  const fields: Record<string, PartialField> = {};
  let i = 0;
  const n = json.length;

  const skipWs = () => {
    while (i < n && /\s/.test(json[i]!)) i++;
  };

  /** Decode a string starting at the opening quote. Leaves `i` after the closing quote. */
  const readString = (): PartialField => {
    i++; // opening quote
    let out = '';
    while (i < n) {
      const ch = json[i]!;
      if (ch === '"') {
        i++;
        return { value: out, complete: true };
      }
      if (ch === '\\') {
        const next = json[i + 1];
        if (next === undefined) break; // escape cut off; wait for more input
        if (next === 'u') {
          const hex = json.slice(i + 2, i + 6);
          if (hex.length < 4 || !/^[0-9a-fA-F]{4}$/.test(hex)) break;
          out += String.fromCharCode(parseInt(hex, 16));
          i += 6;
          continue;
        }
        out += SIMPLE_ESCAPES[next] ?? next;
        i += 2;
        continue;
      }
      out += ch;
      i++;
    }
    i = n;
    return { value: out, complete: false };
  };

  /** Skip a non-string value (number, literal, array, object) at the top level. */
  const skipValue = () => {
    let depth = 0;
    while (i < n) {
      const ch = json[i]!;
      if (ch === '"') {
        readString();
        continue;
      }
      if (ch === '{' || ch === '[') depth++;
      else if (ch === '}' || ch === ']') {
        if (depth === 0) return;
        depth--;
      } else if (ch === ',' && depth === 0) return;
      i++;
    }
  };

  skipWs();
  if (json[i] !== '{') return fields;
  i++;
  while (i < n) {
    skipWs();
    if (json[i] === '}') break;
    if (json[i] === ',') {
      i++;
      continue;
    }
    if (json[i] !== '"') break;
    const key = readString();
    if (!key.complete) break;
    skipWs();
    if (json[i] !== ':') break;
    i++;
    skipWs();
    if (i >= n) break;
    if (json[i] === '"') fields[key.value] = readString();
    else skipValue();
  }
  return fields;
}
